import type { BodyPose } from './bodyPoseFrame.ts';
import {
  DwellTracker, cursorFromPose, hitTest, moveFocus, remoteKey, type MenuItem,
} from './headlessMenu.ts';

export interface MenuScreenButton {
  id: string;
  label: string;
  sub?: string;
}

export interface MenuScreen {
  /** Повторный `show()` того же экрана ничего не сбрасывает (`updateLobbyHints` зовёт часто). */
  key: string;
  title: string;
  subtitle?: string;
  buttons: readonly MenuScreenButton[];
  focusId?: string;
  /** Защитная задержка: столько мс после показа нажатия (рукой и OK) не принимаются. */
  guardMs?: number;
  /** Поверх идущей игры — полупрозрачная подложка вместо глухой. */
  overGame?: boolean;
  onPick(id: string): void;
  onBack?(): void;
}

/*
 * Слой: над лобби (#lobby 100) и над «выберите игру на телефоне» (#lobby-choose-overlay 105), который меню заменяет;
 * под отсчётом, «соединение потеряно» и «подключение…» (110+).
 */

/** После нажатия — пауза, чтобы та же рука/кнопка не нажала следующий экран. */
const AFTER_PICK_GUARD_MS = 500;

const STYLE = `
.hl-menu{position:fixed;inset:0;z-index:106;display:flex;flex-direction:column;align-items:center;
  justify-content:center;gap:3vh;background:linear-gradient(180deg,#0b1440,#1b0f4a);color:#fff;
  font-family:-apple-system,system-ui,Arial,sans-serif}
.hl-menu--over{background:rgba(8,6,30,.72)}
.hl-menu__title{font-size:6vh;font-weight:800;text-align:center;margin:0}
.hl-menu__sub{font-size:2.6vh;opacity:.8;text-align:center;max-width:70vw;margin:0}
.hl-menu__row{display:flex;flex-wrap:wrap;justify-content:center;gap:3vw;max-width:90vw}
.hl-btn{position:relative;min-width:22vw;min-height:20vh;padding:3vh 3vw;box-sizing:border-box;border-radius:3vh;
  background:rgba(255,255,255,.1);border:.6vh solid rgba(255,255,255,.18);display:flex;flex-direction:column;
  align-items:center;justify-content:center;gap:1vh;overflow:hidden;cursor:pointer}
.hl-btn--focus{border-color:#ffe566;background:rgba(255,229,102,.16);transform:scale(1.05)}
.hl-btn__label{font-size:4.4vh;font-weight:800;text-align:center}
.hl-btn__sub{font-size:2.4vh;opacity:.8;text-align:center}
.hl-btn__dwell{position:absolute;left:0;bottom:0;height:1.2vh;width:100%;background:#ffe566;transform-origin:0 50%;
  transform:scaleX(0)}
.hl-cursor{position:fixed;left:0;top:0;width:6vh;height:6vh;margin:-3vh 0 0 -3vh;border-radius:50%;z-index:107;
  background:rgba(255,229,102,.85);border:.5vh solid #fff;box-shadow:0 0 2vh rgba(0,0,0,.5);pointer-events:none;
  display:none}
`;

/**
 * Экран выбора на headless-ТВ (GEN-18912): кнопки с фокусом D-pad и рукой-курсором (dwell). Один компонент для
 * каталога игр, выбора режима/уровня и экрана итога. Курсор по `BodyPoseStore`; без позы он скрыт. Пульт и рука
 * работают одновременно: фокус там, куда указал последний ввод.
 */
export class HeadlessMenuOverlay {
  private root: HTMLDivElement | null = null;

  private cursor: HTMLDivElement | null = null;

  private screen: MenuScreen | null = null;

  private buttons = new Map<string, HTMLDivElement>();

  private focusId: string | null = null;

  private readonly dwell = new DwellTracker();

  /** Кнопка под рукой (для гистерезиса); фокус D-pad может быть на другой. */
  private dwellHovered: string | null = null;

  private guardUntilMs = 0;

  private raf = 0;

  private styleEl: HTMLStyleElement | null = null;

  /** Последняя записанная полоса dwell по кнопкам: без записи стиля, пока значение то же. */
  private barScale = new Map<string, number>();

  constructor(private readonly getPose: (nowMs: number) => BodyPose | null) {}

  get screenKey(): string | null {
    return this.screen?.key ?? null;
  }

  isVisible(): boolean {
    return this.screen !== null;
  }

  show(screen: MenuScreen): void {
    if (this.screen?.key === screen.key) {
      this.screen = screen;
      return;
    }
    const root = this.ensureRoot();
    const now = performance.now();
    this.screen = screen;
    // Задержка после нажатия на прошлом экране переносится на новый.
    this.guardUntilMs = Math.max(this.guardUntilMs, now + (screen.guardMs ?? 0));
    this.dwell.reset(now, screen.guardMs ?? 0);
    this.dwellHovered = null;
    this.barScale = new Map();
    root.classList.toggle('hl-menu--over', screen.overGame === true);
    root.replaceChildren();

    const title = document.createElement('h1');
    title.className = 'hl-menu__title';
    title.textContent = screen.title;
    root.appendChild(title);
    if (screen.subtitle) {
      const sub = document.createElement('p');
      sub.className = 'hl-menu__sub';
      sub.textContent = screen.subtitle;
      root.appendChild(sub);
    }
    const row = document.createElement('div');
    row.className = 'hl-menu__row';
    this.buttons = new Map();
    screen.buttons.forEach((b) => {
      const el = document.createElement('div');
      el.className = 'hl-btn';
      const label = document.createElement('div');
      label.className = 'hl-btn__label';
      label.textContent = b.label;
      el.appendChild(label);
      if (b.sub) {
        const sub = document.createElement('div');
        sub.className = 'hl-btn__sub';
        sub.textContent = b.sub;
        el.appendChild(sub);
      }
      // Полоса dwell — всегда последний ребёнок (её ищет `tick`).
      const bar = document.createElement('div');
      bar.className = 'hl-btn__dwell';
      el.appendChild(bar);
      // Мышь — для отладки в браузере.
      el.addEventListener('click', () => this.pick(b.id));
      row.appendChild(el);
      this.buttons.set(b.id, el);
    });
    root.appendChild(row);
    root.style.display = 'flex';

    this.setFocus(screen.focusId ?? screen.buttons[0]?.id ?? null);
    if (!this.raf) this.raf = requestAnimationFrame(this.tick);
  }

  hide(): void {
    this.screen = null;
    this.dwellHovered = null;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.root) this.root.style.display = 'none';
    if (this.cursor) this.cursor.style.display = 'none';
  }

  /** Убирает с страницы всё, что добавил оверлей (повторный запуск ТВ-приложения, HMR). */
  dispose(): void {
    this.hide();
    window.removeEventListener('keydown', this.onKeyDown);
    this.root?.remove();
    this.cursor?.remove();
    this.styleEl?.remove();
    this.root = null;
    this.cursor = null;
    this.styleEl = null;
  }

  private ensureRoot(): HTMLDivElement {
    if (this.root) return this.root;
    const style = document.createElement('style');
    style.textContent = STYLE;
    document.head.appendChild(style);
    this.styleEl = style;
    const root = document.createElement('div');
    root.className = 'hl-menu';
    this.cursor = document.createElement('div');
    this.cursor.className = 'hl-cursor';
    document.body.append(root, this.cursor);
    window.addEventListener('keydown', this.onKeyDown);
    this.root = root;
    return root;
  }

  private readonly tick = (now: number): void => {
    this.raf = 0;
    if (!this.screen) return;
    // Следующий кадр — сразу: `pick` ниже может показать другой экран, и тот не должен запускать вторую цепочку.
    this.raf = requestAnimationFrame(this.tick);
    const point = cursorFromPose(this.getPose(now));
    const items = this.items();
    const hit = hitTest(items, point, this.dwellHovered);
    const { hoveredId, progress, fired } = this.dwell.update(hit, now);
    // Фокус к руке — только когда она перешла на кнопку: неподвижная рука не отбирает фокус у пульта.
    if (hoveredId !== this.dwellHovered && hoveredId) this.setFocus(hoveredId);
    this.dwellHovered = hoveredId;
    this.buttons.forEach((el, id) => {
      const scale = id === hoveredId ? progress : 0;
      if (this.barScale.get(id) === scale) return;
      this.barScale.set(id, scale);
      const bar = el.lastElementChild as HTMLElement;
      bar.style.transform = `scaleX(${scale})`;
    });
    if (this.cursor) {
      this.cursor.style.display = point ? 'block' : 'none';
      if (point) {
        this.cursor.style.transform = `translate(${point.x * window.innerWidth}px,${point.y * window.innerHeight}px)`;
      }
    }
    if (fired) this.pick(fired);
  };

  /** Рамки кнопок в долях экрана — каждый кадр (кнопок мало, а раскладка меняется при ресайзе). */
  private items(): MenuItem[] {
    const W = window.innerWidth || 1;
    const H = window.innerHeight || 1;
    return [...this.buttons].map(([id, el]) => {
      const r = el.getBoundingClientRect();
      return {
        id,
        rect: {
          x: r.left / W, y: r.top / H, w: r.width / W, h: r.height / H,
        },
      };
    });
  }

  private setFocus(id: string | null): void {
    this.focusId = id;
    this.buttons.forEach((el, bid) => el.classList.toggle('hl-btn--focus', bid === id));
  }

  private pick(id: string): void {
    const { screen } = this;
    const now = performance.now();
    if (!screen || now < this.guardUntilMs) return;
    this.guardUntilMs = now + AFTER_PICK_GUARD_MS;
    this.dwell.reset(now, AFTER_PICK_GUARD_MS);
    screen.onPick(id);
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    const { screen } = this;
    if (!screen) return;
    const key = remoteKey(e);
    if (!key) return;
    // «Назад» без обработчика (каталог) оставляем ТВ: webOS/Tizen по нему выходят из приложения.
    if (key === 'back' && !screen.onBack) return;
    e.preventDefault();
    // Ввод с пульта: кнопка под лежащей рукой не дожимается сама, пока рука не уйдёт с неё.
    this.dwell.reset(performance.now());
    if (key === 'ok') {
      if (this.focusId) this.pick(this.focusId);
    } else if (key === 'back') {
      screen.onBack?.();
    } else {
      this.setFocus(moveFocus(this.items(), this.focusId, key));
    }
  };
}
